/**
 * Lightbox — full-screen image overlay shared across every case study.
 *
 * Mounted into a singleton root on <body> by bootstrap.jsx. Driven by clicks
 * intercepted on any <img> inside a [data-jt-island^="case-study-"] container.
 *
 * Modes:
 *  - Fit (default): image at object-contain, max 92vh × 92vw — never larger
 *    than its natural resolution. Tall images become narrow because they get
 *    scaled down to fit viewport height.
 *  - Zoomed (click-to-toggle): image renders at its natural width inside a
 *    scrollable container, so users can read fine print on long page
 *    screenshots. Click again to return to Fit. The mouse cursor changes
 *    between zoom-in / zoom-out depending on the current mode. Added
 *    2026-05-06 for Advisent's tall page-screenshot tiles.
 *
 * Close: Escape key, backdrop click, or close button (top-right pill).
 * Body scroll lock while open.
 * Subtle scale/opacity entry + exit.
 */

import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

function CloseIcon() {
	return (
		<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
			<path d="M18 6 6 18" />
			<path d="m6 6 12 12" />
		</svg>
	);
}

export default function Lightbox( { src, alt, onClose } ) {
	const [ zoomed, setZoomed ] = useState( false );

	// Reset zoom every time a new image opens.
	useEffect( () => {
		setZoomed( false );
	}, [ src ] );

	useEffect( () => {
		if ( ! src ) return;
		const handleKey = ( e ) => {
			if ( e.key === 'Escape' ) onClose();
		};
		document.addEventListener( 'keydown', handleKey );
		const prevOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		return () => {
			document.removeEventListener( 'keydown', handleKey );
			document.body.style.overflow = prevOverflow;
		};
	}, [ src, onClose ] );

	return (
		<AnimatePresence>
			{ src ? (
				<motion.div
					key="lightbox"
					initial={ { opacity: 0 } }
					animate={ { opacity: 1 } }
					exit={ { opacity: 0 } }
					transition={ { duration: 0.22, ease: [ 0.16, 1, 0.3, 1 ] } }
					className={ `fixed inset-0 z-[200] bg-black/95 backdrop-blur-md ${ zoomed ? 'overflow-auto' : 'flex items-center justify-center p-4 md:p-8' }` }
					onClick={ onClose }
					role="dialog"
					aria-modal="true"
					aria-label="Image preview"
				>
					{ /* Close button — fixed-positioned so it stays visible when scrolling a zoomed image. */ }
					<motion.button
						type="button"
						onClick={ ( e ) => { e.stopPropagation(); onClose(); } }
						initial={ { opacity: 0, y: -8 } }
						animate={ { opacity: 1, y: 0 } }
						exit={ { opacity: 0 } }
						transition={ { duration: 0.28, delay: 0.08 } }
						className="fixed right-5 top-5 z-10 inline-flex h-12 w-12 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white backdrop-blur transition hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white md:right-8 md:top-8"
						aria-label="Close image preview"
					>
						<CloseIcon />
					</motion.button>

					{ /* Image — Fit mode (default) centers via flex parent and caps at
					     92vh × 92vw so the whole image fits. Zoomed mode caps width at
					     the viewport (100vw) and lets height scale freely, so tall page
					     screenshots fill the screen horizontally and the user scrolls
					     vertically to read top→bottom. We never upscale past natural
					     size: `w-auto` keeps the image's intrinsic width as the upper
					     bound, and `max-w-[100vw]` clamps it to the viewport on screens
					     narrower than the image. Updated 2026-05-12 — page screenshots
					     are now 1800px wide and overflowed most viewports horizontally
					     under the old `max-w-none` zoom rule. */ }
					{ zoomed ? (
						<div className="flex min-h-full w-full items-start justify-center">
							<motion.img
								key="img-zoomed"
								src={ src }
								alt={ alt || '' }
								initial={ { opacity: 0 } }
								animate={ { opacity: 1 } }
								exit={ { opacity: 0 } }
								transition={ { duration: 0.22 } }
								className="block h-auto w-auto max-w-[100vw] flex-shrink-0 cursor-zoom-out"
								onClick={ ( e ) => { e.stopPropagation(); setZoomed( false ); } }
								draggable={ false }
							/>
						</div>
					) : (
						<motion.img
							key="img-fit"
							src={ src }
							alt={ alt || '' }
							initial={ { opacity: 0, scale: 0.96 } }
							animate={ { opacity: 1, scale: 1 } }
							exit={ { opacity: 0, scale: 0.96 } }
							transition={ { duration: 0.32, ease: [ 0.16, 1, 0.3, 1 ] } }
							className="block max-h-[92vh] max-w-[92vw] cursor-zoom-in rounded-[1.5rem] object-contain shadow-[0_60px_140px_rgba(0,0,0,0.55)]"
							onClick={ ( e ) => { e.stopPropagation(); setZoomed( true ); } }
							draggable={ false }
						/>
					) }

					{ /* Hint — fixed so it stays visible when zoomed image is scrolling. */ }
					<motion.div
						initial={ { opacity: 0, y: 8 } }
						animate={ { opacity: 1, y: 0 } }
						exit={ { opacity: 0 } }
						transition={ { duration: 0.28, delay: 0.12 } }
						className="pointer-events-none fixed bottom-6 left-1/2 -translate-x-1/2 text-[11px] font-black uppercase tracking-[0.22em] text-white/55"
					>
						{ zoomed ? 'Click image to fit · ESC to close' : 'Click image to zoom · ESC to close' }
					</motion.div>
				</motion.div>
			) : null }
		</AnimatePresence>
	);
}
